import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Grid,
  Chip,
} from '@mui/material';
import { skillsApi } from '../../services/api/skillsApi';
import { useAlert } from '../../contexts/AlertContext';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';
import { 
  getReviewTypeColor,
  getReviewTypeTextColor,
  getReviewTypeBorderColor
} from '../../constants/reviewTypes';
import { COLORS } from '../../constants/radar';

const SessionTemplateSelection = ({ 
  clusterId, 
  reviewType, 
  selectedTemplate, 
  onTemplateSelect, 
  onTemplateChange,
  readOnly = false 
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showAlert } = useAlert();

  // Handle read-only mode separately
  useEffect(() => {
    if (readOnly && selectedTemplate) {
      setTemplates([selectedTemplate]);
      setLoading(false);
      return;
    }
  }, [readOnly, selectedTemplate]);

  useEffect(() => {
    const fetchTemplates = async () => {
      // Skip fetching if in read-only mode
      if (readOnly) {
        return;
      }

      // Require reviewType, but clusterId can be null (for individual mode)
      if (!reviewType) {
        setTemplates([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('SessionTemplateSelection: Fetching templates with:', { clusterId, reviewType, selectedTemplate });
        const data = await skillsApi.getSkillsMatrixSessionsByCluster(clusterId, reviewType);
        console.log('SessionTemplateSelection: Fetched templates:', data);
        setTemplates(data || []);
      } catch (err) {
        console.error('SessionTemplateSelection: Error fetching templates:', err);
        setError(err.message || 'Failed to fetch session templates');
        showAlert(err.message || 'Failed to fetch session templates', 'error');
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [clusterId, reviewType, readOnly, showAlert, selectedTemplate]);

  const handleTemplateSelect = (templateId) => {
    if (readOnly) return;
    
    const selected = templates.find(template => template.id === templateId);
    
    // Check if this is a template change (different from currently selected)
    const isTemplateChange = selectedTemplate && selectedTemplate.id !== templateId;
    
    // Call the template selection callback
    onTemplateSelect(selected);
    
    // If this is a template change and we have the onTemplateChange callback, call it to reset data
    if (isTemplateChange && onTemplateChange) {
      onTemplateChange();
    }
  };

  if (!reviewType) {
    return (
      <EmptyState
        title="Please select review type"
        description="Choose a review type to see available session templates."
      />
    );
  }

  if (loading) {
    return (
      <LoadingSpinner message="Loading session templates..." />
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Failed to load session templates"
        description={error}
      />
    );
  }

  if (templates.length === 0) {
    return (
      <EmptyState
        title="No published session templates found"
        description="Please check back later or contact your administrator for assistance."
      />
    );
  }

  return (
    <FormControl component="fieldset" fullWidth>
      <RadioGroup
        value={selectedTemplate?.id || ''}
        onChange={(e) => handleTemplateSelect(e.target.value)}
      >
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {templates
            .filter(template => {
              // In read-only mode, only show the selected template if one exists
              if (readOnly && selectedTemplate) {
                return template.id === selectedTemplate.id;
              }
              // Otherwise show all templates
              return true;
            })
            .map((template) => {
            const isSelected = selectedTemplate?.id === template.id;
            
            return (
              <Grid item xs={12} sm={6} md={6} key={template.id}>
                <Card 
                  onClick={() => !readOnly && handleTemplateSelect(template.id)}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid',
                    borderColor: isSelected 
                      ? getReviewTypeBorderColor(template.reviewType)
                      : '#e2e8f0',
                    backgroundColor: isSelected 
                      ? getReviewTypeColor(template.reviewType)
                      : '#fff',
                    cursor: readOnly ? 'default' : 'pointer',
                    transition: 'border-color 0.2s ease, background-color 0.2s ease',
                    position: 'relative',
                    boxShadow: 'none',
                    opacity: readOnly ? 0.8 : 1, // Visual indicator for read-only
                    '&:hover': readOnly ? {} : { 
                      borderColor: getReviewTypeBorderColor(template.reviewType),
                      backgroundColor: isSelected 
                        ? getReviewTypeColor(template.reviewType)
                        : '#f8fafc'
                    }
                  }}
                >
                  <CardContent sx={{ 
                    p: 3, 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column'
                  }}>
                    {/* Radio button and title */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                      {!readOnly && (
                        <FormControlLabel
                          value={template.id}
                          control={
                            <Radio 
                              sx={{
                                color: '#94a3b8',
                                '&.Mui-checked': {
                                  color: getReviewTypeBorderColor(template.reviewType),
                                },
                                p: 0.5
                              }}
                            />
                          }
                          label=""
                          sx={{ mr: 2, mt: 0 }}
                        />
                      )}
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          color: isSelected 
                            ? getReviewTypeTextColor(template.reviewType)
                            : COLORS.PRIMARY,
                          wordBreak: 'break-word',
                          flex: 1
                        }}
                      >
                        {template.name}
                      </Typography>
                      {readOnly && (
                        <Chip
                          label="Selected"
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: 20,
                            backgroundColor: '#f0f9ff',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                    
                    {/* Description */}
                    {template.description && (
                      <Box sx={{ mb: 2, ml: 4 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#666', 
                            fontStyle: 'italic', 
                            lineHeight: 1.4,
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            maxWidth: '100%'
                          }}
                        >
                          {template.description}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Metadata row - badges grouped consistently */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      alignItems: 'center', 
                      mb: 2,
                      ml: 4,
                      minHeight: '32px' // Ensure consistent height even without version
                    }}>
                      {template.version && (
                        <Chip
                          label={`v.${template.version}`}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: 24,
                            fontWeight: 600,
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            '& .MuiChip-icon': {
                              fontSize: '14px'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </RadioGroup>
    </FormControl>
  );
};

export default SessionTemplateSelection; 